import React from "react";
import {Form, Input} from "reactstrap";
import {connect} from "react-redux";
import moment from "moment";
import {convertAndFormatUTC} from "../../../../util/format";
import ArchiveLink from "./ArchiveLink";
import PRCopyright from "./PRCopyright";
import ScrollingList from "./ScrollingList";
import {
    searchNewsletters,
    resetSearch,
    onChangeNewsletterSearch,
    clearSearch,
} from "../../../../redux/actions/search";
import {
    getCurrentNewsletterGroupingId,
    isCurrentNewsletterTemporary,
    getIsNewsletterHidden, getAudioReaderBaseUrl, getCurrentNewsletterEdition,
} from "../../../../redux/selectors/newsletters";
import {
    getUrlAccessKey,
} from "../../../../redux/selectors/navigation";
import {
    getSearchResults,
    getSearchError,
    isSearchActive,
    getSearchTerm,
} from "../../../../redux/selectors/search";
import {useElementPosition} from "use-element-position";
import { shouldIncludeAudioReaderLink } from "../../../../redux/selectors/template";

/**
 * Component to output Nest search input/results
 * Extends React.Component to use component specific state
 */
class Search extends React.Component {
    constructor (props) {
        super(props);

        this.state = {
            termTooShort: false,
        };
    }

    /**
     * change handler for search input
     * @param  {} event - synthetic event
     */
    onChange = event => {
        const searchTerm = event.target.value;

        if (searchTerm.trim().length) {
            this.props.change(searchTerm);
        } else {
            //we consider this the user longer searching
            this.props.clear();
        }
    };

    /**
     * submit handler for search
     * @param  {} event - synthetic event
     */
    onSubmit = event => {
        event.preventDefault();

        const {
            doSearch,
            urlAccessKey,
            newsletterGroupingId,
            toggleActive,
            searchTerm,
            resetSearch,
        } = this.props;

        //search is active and we'll clear any previous search results
        resetSearch();

        const trimmedSearchTerm = searchTerm.trim();

        if (trimmedSearchTerm.length < 3) {
            this.setState({termTooShort: true});

            return;
        }

        this.setState({termTooShort: false});

        doSearch(urlAccessKey, newsletterGroupingId, trimmedSearchTerm);
    };

    render() {
        const {
            isTemporary,
            searchResults,
            searchError,
            searchTerm,
            isNewsletterHidden,
            searchIsActive,
            includeAudioReaderLink,
            audioReaderBaseUrl,
            urlAccessKey,
        } = this.props;


        let inputGroupClass = "input-group-prepend prepend-cursor";

        let placeHolderText = "Search..."

        if (isTemporary) {
            inputGroupClass = `${inputGroupClass} prepend-disabled`;

            placeHolderText = "Search unavailable for previews";
        }

        return (
            <div>
                <Form onSubmit={this.onSubmit}>
                    <div className="input-group w-100 pb-3 px-3">
                        <div className={inputGroupClass} onClick={this.onSubmit}>
                            <span className="input-group-text"><i className="far fa-search"></i></span>
                        </div>

                        <Input className="aside-input"
                                type="search"
                                name="search"
                                placeholder={placeHolderText}
                                disabled={isTemporary || isNewsletterHidden}
                                onChange={this.onChange}
                                value={searchTerm}
                                autoComplete="off" />
                    </div>
                </Form>

                {searchIsActive === true && this.state.termTooShort === true &&
                    <div className="input-group w-100 pb-3 px-3">
                        Searches must be at least three characters long.
                        Please enter a longer search term and try again.
                    </div>
                }

                {searchError !== null &&
                    <div className="input-group w-100 pb-3 px-3">
                        Something went wrong with your search. Please check your search
                        terms to ensure they are correct and try again.
                    </div>
                }

                {searchResults !== null && searchResults.length === 0 &&
                    <div className="input-group w-100 pb-3 px-3">
                        No results were found for your search. Please modify your
                        search terms and try again.
                    </div>
                }

                {
                    includeAudioReaderLink && (
                        <div className="input-group px-4 w-100 mb-4">
                            <a href={`${audioReaderBaseUrl}/${urlAccessKey}?date=${this.props.currentDataEdition}`}
                               style={{ color: "#1967d2" }}
                               target="_blank"
                               rel="noreferrer"
                               className="font-weight-bold">
                                <i className="far fa-volume-up mr-2"></i>
                                Listen in Audio Reader
                            </a>
                        </div>
                    )
                }
                {searchResults !== null && searchResults.length > 0 &&
                    <SearchResultsList props={this.props} />
                }

            </div>
        );
    }
}

const SearchResultsList = ({props}) => {
    const {searchResults} = props;

    return (
        <ScrollingList>
            {searchResults.map((result, index) =>
                <li className="nav-item" key={index}>
                    <div className="section-title text-truncate">
                        {moment(result.edition).format("MMMM D")} Edition
                    </div>
                    <ul className="nav flex-column nested-list">
                        {result.items.map((item, index) =>
                            (<SearchResultItem
                                item={item}
                                editionDate={result.edition}
                                key={index} />)
                        )}
                    </ul>
                </li>
            )}
            <li><PRCopyright /></li>
        </ScrollingList>
    )
}

const SearchResultItem = (props) => {
    const {
        title,
        outletName,
        intakeDate,
        groupingItemId,
    } = props.item;

    const anchorId = `article_${groupingItemId}`;

    /**
     * note that search results are for the current newsletter so we don't need to pass
     * groupingId when creating links
     */
    return (
        <li className="nav-item">
            <ArchiveLink
                hash={anchorId}
                className="nav-article-link"
                date={props.editionDate}
                title={title}
                outletName={outletName}
                formattedDate={convertAndFormatUTC(intakeDate, "MMMM D, YYYY")} />
        </li>
    )
};

/**
 * mapStateToProps returns the parts of the state that will be available as props
 * @param state
 * @returns {}
 */
const mapStateToProps = (state) => {
    return {
        newsletterGroupingId: getCurrentNewsletterGroupingId(state),
        urlAccessKey: getUrlAccessKey(state),
        isTemporary: isCurrentNewsletterTemporary(state),
        searchTerm: getSearchTerm(state),
        searchResults: getSearchResults(state),
        searchError: getSearchError(state),
        searchIsActive: isSearchActive(state),
        isNewsletterHidden: getIsNewsletterHidden(state),
        includeAudioReaderLink: shouldIncludeAudioReaderLink(state),
        audioReaderBaseUrl: getAudioReaderBaseUrl(state),
        currentDataEdition: getCurrentNewsletterEdition(state),
    };
};

/**
 * mapDispatchToProps exposes the actions we want to call as props
 * @param state
 * @returns {{navigation: *}}
 */
const mapDispatchToProps = (dispatch, ownProps) => {
    return {
        doSearch: (accessKey, newsletterGroupingId, term) => {
            dispatch(searchNewsletters(accessKey, newsletterGroupingId, term))
        },
        resetSearch: () => {
            dispatch(resetSearch())
        },
        change: (searchTerm) => {
            dispatch(onChangeNewsletterSearch(searchTerm))
        },
        clear: () => {
            dispatch(clearSearch())
        },
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(Search);
